import { useTranslation } from "react-i18next";
import { resolveEaPlayerName, type EaLinkRosterRow } from "../../lib/ea-name";
import type { aggregateMatchesStats } from "./aggregateMatchesStats";

// Posizione full EA -> sigla compatta per mobile.
function shortPos(pos: string): string {
  const p = (pos || "").toLowerCase();
  if (p.startsWith("forward") || p.startsWith("striker") || p === "att" || p === "ed" || p === "es" || p === "sp") return "FW";
  if (p.startsWith("midfield") || p === "med" || p === "cc" || p === "trq") return "MID";
  if (p.startsWith("defend") || p === "td" || p === "dc" || p === "ts") return "DEF";
  if (p.startsWith("goal") || p === "por" || p === "gk") return "GK";
  return (pos || "-").slice(0, 3).toUpperCase();
}

export function PlayerAggTable({
  players,
  sortBy,
  rosterLookup,
}: {
  players: ReturnType<typeof aggregateMatchesStats>["players"];
  sortBy: "goals" | "assists" | "rating" | "mom" | "saves";
  rosterLookup?: Map<string, EaLinkRosterRow>;
}) {
  const { t } = useTranslation("mondofc");
  const sorted = [...players].sort((a, b) => {
    if (sortBy === "goals") return b.goals - a.goals;
    if (sortBy === "assists") return b.assists - a.assists;
    if (sortBy === "mom") return b.mom - a.mom;
    if (sortBy === "saves") return b.saves - a.saves;
    const ar = a.ratingCount > 0 ? a.ratingSum / a.ratingCount : 0;
    const br = b.ratingCount > 0 ? b.ratingSum / b.ratingCount : 0;
    return br - ar;
  });
  return (
    // min-w-0 lungo la catena previene il "push" della tabella sul parent
    // (Card/Grid) che su mobile causava il layout shift segnalato.
    <div className="min-w-0 overflow-x-auto -mx-1">
      <table className="w-full text-xs sm:text-sm">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left font-mono uppercase text-[11px] sm:text-xs tracking-wider py-1 pr-1 sm:pr-2">{t("playerAgg.name")}</th>
            <th className="text-left font-mono uppercase text-[11px] sm:text-xs tracking-wider px-0.5 sm:px-1">{t("playerAgg.pos")}</th>
            <th className="text-right font-mono uppercase text-[11px] sm:text-xs tracking-wider px-0.5 sm:px-1">{t("playerAgg.pt")}</th>
            <th className="text-right font-mono uppercase text-[11px] sm:text-xs tracking-wider px-0.5 sm:px-1">{t("playerAgg.g")}</th>
            <th className="text-right font-mono uppercase text-[11px] sm:text-xs tracking-wider px-0.5 sm:px-1">{t("playerAgg.a")}</th>
            <th className="text-right font-mono uppercase text-[11px] sm:text-xs tracking-wider px-0.5 sm:px-1">{t("playerAgg.mom")}</th>
            <th className="text-right eb-data text-[13px] px-1 hidden md:table-cell">{t("playerAgg.passPct")}</th>
            <th className="text-right eb-data text-[13px] px-1 hidden md:table-cell">{t("playerAgg.sav")}</th>
            <th className="text-right font-mono uppercase text-[11px] sm:text-xs tracking-wider px-0.5 sm:px-1">{t("playerAgg.rating")}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => {
            const passPct = p.passAttempts > 0 ? Math.round((p.passes / p.passAttempts) * 100) : 0;
            const avgR = p.ratingCount > 0 ? p.ratingSum / p.ratingCount : 0;
            const resolved = rosterLookup
              ? resolveEaPlayerName({ playername: p.name, name: p.name }, rosterLookup)
              : { displayName: p.name, linked: false };
            return (
              <tr key={p.name} className="border-t">
                <td className="py-1 sm:py-1.5 pr-1 sm:pr-2 max-w-[90px] sm:max-w-[200px]">
                  <span className="font-medium block truncate">{resolved.displayName}</span>
                  {resolved.linked && resolved.displayName !== p.name && (
                    <span className="block text-[9.5px] sm:text-xs text-muted-foreground font-mono truncate">{p.name}</span>
                  )}
                </td>
                <td className="px-0.5 sm:px-1 text-[9.5px] sm:text-xs uppercase sm:capitalize text-muted-foreground whitespace-nowrap">
                  <span className="sm:hidden">{shortPos(p.pos)}</span>
                  <span className="hidden sm:inline">{p.pos}</span>
                </td>
                <td className="text-right font-tabular px-0.5 sm:px-1">{p.matches}</td>
                <td className="text-right font-tabular px-0.5 sm:px-1 font-semibold">{p.goals}</td>
                <td className="text-right font-tabular px-0.5 sm:px-1">{p.assists}</td>
                <td className="text-right font-tabular px-0.5 sm:px-1">{p.mom}</td>
                <td className="text-right font-tabular px-1 text-muted-foreground hidden md:table-cell">{passPct}%</td>
                <td className="text-right font-tabular px-1 text-muted-foreground hidden md:table-cell">{p.saves}</td>
                <td className="text-right font-tabular px-0.5 sm:px-1">{avgR > 0 ? avgR.toFixed(2) : "-"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
